import { useEffect, useRef } from 'react';
import { theme } from 'antd';
import { RUTA_COBROS_TIPO, type RutaCobroEntry } from '@aid/shared';
import { EmptyState } from '../../../components/EmptyState';
import { coordenadasValidas } from '../../../lib/geo';
import { useLeaflet, type LeafletModule } from '../../../lib/useLeaflet';
import { useT } from '../../../locales';

type LeafletMap = import('leaflet').Map;
type LeafletMarker = import('leaflet').Marker;

interface RutaCobrosMapProps {
  entries: RutaCobroEntry[];
  selectedPaymentId: number | null;
  /** Stable activity key used to focus either a payment or a visit marker. */
  selectedEntryKey?: string | null;
  onSelectPayment: (id: number) => void;
  onSelectEntry?: (entry: RutaCobroEntry) => void;
}

interface EntryWithCoordinate {
  entry: RutaCobroEntry;
  number: number;
  latitud: number;
  longitud: number;
}

const MAP_MAX_ZOOM = 19;
const MAP_FOCUS_ZOOM = 16;

function createPaymentMarkerIcon(
  L: LeafletModule,
  number: number,
  selected: boolean,
  isVisit: boolean,
  primaryColor: string,
  visitColor: string,
  selectedColor: string,
  borderColor: string,
) {
  const size = selected ? 40 : 32;
  const fill = selected ? selectedColor : isVisit ? visitColor : primaryColor;
  return L.divIcon({
    className: '',
    html: `<div style="
      width: ${size}px; height: ${size}px; border-radius: 50%;
      background: ${fill}; border: 3px solid ${borderColor};
      box-shadow: 0 2px 8px rgba(0,0,0,0.5);
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-weight: 700; font-size: ${selected ? 15 : 13}px;
      font-family: ui-sans-serif, system-ui, sans-serif;
      text-shadow: 0 1px 3px rgba(0,0,0,0.8);
      box-sizing: border-box;
    ">${number}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export function RutaCobrosMap({
  entries,
  selectedPaymentId,
  selectedEntryKey,
  onSelectPayment,
  onSelectEntry,
}: RutaCobrosMapProps) {
  const t = useT();
  const { token } = theme.useToken();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Map<string, LeafletMarker>>(new Map());
  const markerNumbersRef = useRef<Map<string, number>>(new Map());
  const onSelectPaymentRef = useRef(onSelectPayment);
  const onSelectEntryRef = useRef(onSelectEntry);
  const translateRef = useRef(t);

  onSelectPaymentRef.current = onSelectPayment;
  onSelectEntryRef.current = onSelectEntry;
  translateRef.current = t;

  const entriesWithCoordinates: EntryWithCoordinate[] = entries.flatMap((entry, index) => {
    const coordinate = coordenadasValidas(entry.latitud, entry.longitud);
    return coordinate
      ? [{ entry, number: index + 1, latitud: coordinate.lat, longitud: coordinate.lng }]
      : [];
  });
  const hasCoordinates = entriesWithCoordinates.length > 0;
  const activeEntryKey =
    selectedEntryKey ??
    (selectedPaymentId === null ? null : `${RUTA_COBROS_TIPO.PAGO}:${selectedPaymentId}`);
  const L = useLeaflet(hasCoordinates);

  useEffect(() => {
    if (!L || !mapRef.current || !hasCoordinates) return;

    const entriesWithCoordinatesForMap: EntryWithCoordinate[] = entries.flatMap((entry, index) => {
      const coordinate = coordenadasValidas(entry.latitud, entry.longitud);
      return coordinate
        ? [{ entry, number: index + 1, latitud: coordinate.lat, longitud: coordinate.lng }]
        : [];
    });

    const map = L.map(mapRef.current, {
      zoomControl: true,
      maxZoom: MAP_MAX_ZOOM,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: MAP_MAX_ZOOM,
    }).addTo(map);

    const bounds = L.latLngBounds([]);
    markersRef.current.clear();
    markerNumbersRef.current.clear();

    for (const item of entriesWithCoordinatesForMap) {
      const isVisit = item.entry.tipo === RUTA_COBROS_TIPO.VISITA;
      const markerKey = `${item.entry.tipo}:${item.entry.id}`;
      const markerLabel = translateRef.current(
        isVisit ? 'dashboard.rutaCobros.visitMarkerLabel' : 'dashboard.rutaCobros.markerLabel',
        { number: item.number },
      );
      const marker = L.marker([item.latitud, item.longitud], {
        icon: createPaymentMarkerIcon(
          L,
          item.number,
          false,
          isVisit,
          token.colorPrimary,
          token.colorWarning,
          token.colorWarning,
          token.colorBgContainer,
        ),
        title: markerLabel,
      });
      marker.bindTooltip(markerLabel, { direction: 'top', offset: [0, -8] });
      marker.on('click', () => {
        if (isVisit) {
          onSelectEntryRef.current?.(item.entry);
        } else {
          onSelectPaymentRef.current(item.entry.id);
        }
      });
      marker.addTo(map);
      markersRef.current.set(markerKey, marker);
      markerNumbersRef.current.set(markerKey, item.number);
      bounds.extend([item.latitud, item.longitud]);
    }

    mapInstanceRef.current = map;
    if (entriesWithCoordinatesForMap.length === 1) {
      const point = entriesWithCoordinatesForMap[0]!;
      map.setView([point.latitud, point.longitud], MAP_FOCUS_ZOOM);
    } else if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current.clear();
      markerNumbersRef.current.clear();
    };
  }, [L, entries, hasCoordinates, token.colorBgContainer, token.colorPrimary, token.colorWarning]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !L) return;

    for (const [id, marker] of markersRef.current) {
      const number = markerNumbersRef.current.get(id);
      if (number === undefined) continue;
      marker.setIcon(
        createPaymentMarkerIcon(
          L,
          number,
          id === activeEntryKey,
          id.startsWith(`${RUTA_COBROS_TIPO.VISITA}:`),
          token.colorPrimary,
          token.colorWarning,
          token.colorWarning,
          token.colorBgContainer,
        ),
      );
    }

    if (activeEntryKey === null) return;
    const selected = entries
      .map((entry, index) => {
        const coordinate = coordenadasValidas(entry.latitud, entry.longitud);
        return coordinate
          ? { entry, number: index + 1, latitud: coordinate.lat, longitud: coordinate.lng }
          : null;
      })
      .find(
        (item): item is EntryWithCoordinate =>
          item !== null && `${item.entry.tipo}:${item.entry.id}` === activeEntryKey,
      );
    const selectedMarker = markersRef.current.get(activeEntryKey);
    if (!selected || !selectedMarker) return;

    map.setView([selected.latitud, selected.longitud], MAP_FOCUS_ZOOM);
    selectedMarker.openTooltip();
  }, [
    L,
    entries,
    selectedPaymentId,
    selectedEntryKey,
    token.colorBgContainer,
    token.colorPrimary,
    token.colorWarning,
  ]);

  if (!hasCoordinates) {
    return (
      <div className="ruta-cobros-map ruta-cobros-map--empty">
        <EmptyState
          compact
          title={t('dashboard.rutaCobros.emptyMapTitle')}
          description={t('dashboard.rutaCobros.emptyMapDescription')}
        />
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      data-testid="ruta-cobros-map"
      className="ruta-cobros-map"
      role="region"
      aria-label={t('dashboard.rutaCobros.mapTitle')}
      aria-busy={L === null}
      style={{
        isolation: 'isolate',
        ['--ruta-cobros-map-border' as string]: token.colorBorderSecondary,
      }}
    />
  );
}
